import axios from "axios";
import { enqueueSnackbar } from "notistack";

const apiUrl = process.env.REACT_APP_API_BASE_URL;

const getUserInfo = () => {
  try {
    const userInfo = localStorage.getItem("userInfo");
    return userInfo ? JSON.parse(userInfo) : null;
  } catch {
    return null;
  }
};

const getAuthToken = () => {
  const userInfo = getUserInfo();
  return userInfo?.access || userInfo?.token || null;
};

export const apiClient = axios.create({
  baseURL: apiUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const ForceLogout = () => {
  localStorage.removeItem("userInfo");
  window.location.href = "/login";
};

const extractAllErrorMessages = (obj) => {
  const messages = [];
  
  if (!obj || typeof obj !== 'object') {
    return messages;
  }
  
  for (const [key, value] of Object.entries(obj)) {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (typeof item === 'string') {
          messages.push(item);
        } else if (typeof item === 'object' && item !== null) {
          const nested = extractAllErrorMessages(item);
          messages.push(...nested);
        }
      });
    } else if (typeof value === 'string') {
      if (value.includes('{') || value.includes('[')) {
        try {
          const parsed = JSON.parse(value);
          if (typeof parsed === 'object' && parsed !== null) {
            const nested = extractAllErrorMessages(parsed);
            messages.push(...nested);
          } else {
            messages.push(value);
          }
        } catch {
          messages.push(value);
        }
      } else {
        messages.push(value);
      }
    } else if (typeof value === 'object' && value !== null) {
      const nested = extractAllErrorMessages(value);
      messages.push(...nested);
    }
  }
  
  return messages;
};

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.isAxiosError && error.response) {
      const statusCode = error.response.status;
      const errorData = error.response.data;

      if (statusCode === 401) {
        const isLoginRequest = error.config?.url?.includes('/auth/login/');
        if (!isLoginRequest) {
          ForceLogout();
        }
        return Promise.reject(error);
      }

      if (statusCode === 400 && errorData && typeof errorData === 'object') {
        const errorMessages = extractAllErrorMessages(errorData);
        
        if (errorMessages.length > 0) {
          const uniqueMessages = [...new Set(errorMessages)];
          uniqueMessages.forEach((msg) => {
            if (msg && msg.trim()) {
              enqueueSnackbar(msg, { 
                variant: "error",
                autoHideDuration: 5000,
              });
            }
          });
        } else {
          enqueueSnackbar("Validation error occurred", { variant: "error" });
        }
        
        return Promise.reject(error);
      }

      const allMessages = extractAllErrorMessages(errorData);
      if (allMessages.length > 0) {
        const uniqueMessages = [...new Set(allMessages)];
        uniqueMessages.forEach((msg) => {
          if (msg && msg.trim()) {
            enqueueSnackbar(msg, { variant: "error" });
          }
        });
      } else {
        enqueueSnackbar("An error occurred", { variant: "error" });
      }
    } else {
      enqueueSnackbar(error.message || "Network error", { variant: "error" });
    }
    
    return Promise.reject(error);
  }
);